import { AzureOpenAI } from 'openai';
import { LLMProvider, LLMChatRequest, StreamCallbacks, LLMConfig, ToolSchema } from '../../types';

type OpenAIMessageRole = 'system' | 'user' | 'assistant' | 'tool';

interface OpenAIMessage {
  role: OpenAIMessageRole;
  content: string | null;
  tool_calls?: Array<{
    id: string;
    type: 'function';
    function: {
      name: string;
      arguments: string;
    };
  }>;
  tool_call_id?: string;
}

const convertToOpenAITools = (tools?: ToolSchema[]) => {
  if (!tools || tools.length === 0) return undefined;

  return tools.map((tool) => ({
    type: 'function' as const,
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
    },
  }));
};

const convertToOpenAIMessages = (request: LLMChatRequest): OpenAIMessage[] => {
  const messages: OpenAIMessage[] = [{ role: 'system', content: request.systemPrompt }];

  // Track tool_call IDs from assistant messages for validation
  const expectedToolCallIds = new Set<string>();

  for (const msg of request.messages) {
    if (msg.role === 'user') {
      messages.push({ role: 'user', content: msg.content });
    } else if (msg.role === 'assistant') {
      const hasToolCalls = msg.toolCalls && msg.toolCalls.length > 0;

      // Azure requires content to be null when there are tool_calls, or a string otherwise
      const assistantMsg: OpenAIMessage = {
        role: 'assistant',
        content: hasToolCalls ? msg.content || null : msg.content,
      };

      if (hasToolCalls) {
        assistantMsg.tool_calls = msg.toolCalls!.map((tc) => {
          expectedToolCallIds.add(tc.id);
          return {
            id: tc.id,
            type: 'function' as const,
            function: {
              name: tc.name,
              arguments: typeof tc.arguments === 'string' ? tc.arguments : JSON.stringify(tc.arguments),
            },
          };
        });
      }
      messages.push(assistantMsg);
    } else if (msg.role === 'tool' && msg.toolResult) {
      const toolCallId = msg.toolResult.toolCallId || '';

      // Validate tool_call_id matches an expected ID
      if (!toolCallId) {
        console.error('Tool result missing toolCallId:', msg.toolResult);
      } else if (!expectedToolCallIds.has(toolCallId)) {
        console.warn('Tool result has unmatched toolCallId:', toolCallId, 'Expected:', Array.from(expectedToolCallIds));
      }

      // Stringify the result content
      const resultContent =
        typeof msg.toolResult.result === 'string' ? msg.toolResult.result : JSON.stringify(msg.toolResult.result);

      messages.push({
        role: 'tool',
        content: resultContent,
        tool_call_id: toolCallId,
      });
    }
  }

  // Debug: log messages being sent with message count
  console.log(`Azure OpenAI sending ${messages.length} messages:`, JSON.stringify(messages, null, 2));

  return messages;
};

export const createAzureOpenAIProvider = (config: LLMConfig): LLMProvider => {
  let abortController: AbortController | null = null;

  const client = new AzureOpenAI({
    endpoint: config.baseUrl,
    apiKey: config.apiKey,
    deployment: config.deployment || config.model,
    apiVersion: config.apiVersion || '2024-04-01-preview',
    dangerouslyAllowBrowser: true,
  });

  const chat = async (request: LLMChatRequest, callbacks: StreamCallbacks): Promise<void> => {
    abortController = new AbortController();

    try {
      const messages = convertToOpenAIMessages(request);
      const tools = convertToOpenAITools(request.tools);

      const response = await client.chat.completions.create(
        {
          model: config.model,
          messages: messages as Parameters<typeof client.chat.completions.create>[0]['messages'],
          tools: tools as Parameters<typeof client.chat.completions.create>[0]['tools'],
          stream: true,
          temperature: config.temperature ?? 0.7,
          max_completion_tokens: config.maxTokens ?? 2048,
        },
        {
          signal: abortController.signal,
        },
      );

      let fullContent = '';
      const toolCallsInProgress: Map<number, { id: string; name: string; arguments: string }> = new Map();

      for await (const chunk of response) {
        const choice = chunk.choices[0];
        if (!choice) continue;

        const delta = choice.delta;

        if (delta?.content) {
          fullContent += delta.content;
          callbacks.onToken(delta.content);
        }

        if (delta?.tool_calls) {
          for (const tc of delta.tool_calls) {
            if (!toolCallsInProgress.has(tc.index)) {
              toolCallsInProgress.set(tc.index, {
                id: tc.id || '',
                name: tc.function?.name || '',
                arguments: '',
              });
            }

            const existing = toolCallsInProgress.get(tc.index);
            if (existing) {
              if (tc.id) existing.id = tc.id;
              if (tc.function?.name) existing.name = tc.function.name;
              if (tc.function?.arguments) existing.arguments += tc.function.arguments;
            }
          }
        }

        if (choice.finish_reason === 'tool_calls') {
          for (const [, tc] of toolCallsInProgress) {
            let args: Record<string, unknown>;
            try {
              args = JSON.parse(tc.arguments);
            } catch {
              args = {};
            }

            callbacks.onToolCall({
              id: tc.id,
              name: tc.name,
              arguments: args,
            });
          }
          toolCallsInProgress.clear();
        }
      }

      callbacks.onComplete(fullContent);
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return;
      }
      // Log detailed error information for debugging
      console.error('Azure OpenAI error:', error);
      if (error && typeof error === 'object') {
        const apiError = error as { status?: number; message?: string; error?: { message?: string; code?: string } };
        console.error('Status:', apiError.status);
        console.error('Message:', apiError.message);
        console.error('Error details:', apiError.error);
      }
      callbacks.onError(error as Error);
    } finally {
      abortController = null;
    }
  };

  const cancelRequest = (): void => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  return { chat, cancelRequest };
};
